/** The bell in the header (tasks and notifications spec §1): unread count every 30 s and on focus; the latest
 * 20 in a menu; opening one marks it read and goes to it; Mark all read clears them. */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Bell, CheckCheck } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { api, unwrap } from '@/lib/api/client'
import { formatRelative } from '@/lib/format'
import { t } from '@/lib/text'
import { cn } from '@/lib/utils'

const LATEST = 20

export function NotificationBell() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const count = useQuery({
    queryKey: ['notifications', 'count'],
    queryFn: async () => unwrap(await api.GET('/api/v1/notifications/unread-count/')),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  })
  const list = useQuery({
    queryKey: ['notifications', 'list'],
    enabled: open,
    queryFn: async () => unwrap(await api.GET('/api/v1/notifications/', { params: { query: { page_size: LATEST } } })),
  })
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['notifications'] })
  const read = useMutation({
    mutationFn: async (id: number) => unwrap(await api.POST('/api/v1/notifications/{id}/read/', { params: { path: { id } } })),
    onSettled: refresh,
  })
  const readAll = useMutation({
    mutationFn: async () => unwrap(await api.POST('/api/v1/notifications/read-all/')),
    onSettled: refresh,
  })
  const unread = count.data?.count ?? 0
  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger
        aria-label={unread ? t('notifications.bell.open_unread', { count: unread }) : t('notifications.bell.title')}
        className="relative inline-flex size-9 items-center justify-center rounded-md hover:bg-accent focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 max-md:size-11"
      >
        <Bell className="size-4" aria-hidden />
        {unread > 0 && (
          <span aria-hidden className="tabular absolute top-1 right-1 min-w-4 rounded-full bg-destructive px-1 text-[10px] font-semibold leading-4 text-destructive-foreground">
            {unread > 99 ? t('notifications.bell.many') : unread}
          </span>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 max-w-[calc(100vw-1rem)]">
        <div className="flex items-center justify-between gap-2">
          <DropdownMenuLabel className="text-foreground">{t('notifications.bell.title')}</DropdownMenuLabel>
          {unread > 0 && (
            <DropdownMenuItem className="text-xs" onSelect={() => readAll.mutate()}>
              <CheckCheck aria-hidden />
              {t('notifications.bell.mark_all')}
            </DropdownMenuItem>
          )}
        </div>
        <DropdownMenuSeparator />
        {list.data?.results.length ? (
          <div className="max-h-96 overflow-y-auto">
            {list.data.results.map((notification) => (
              <DropdownMenuItem
                key={notification.id}
                className="flex flex-col items-start gap-0.5 py-2"
                onSelect={() => {
                  if (!notification.read_at) read.mutate(notification.id)
                  navigate(notification.url)
                }}
              >
                <span className={cn('flex w-full items-start gap-2 text-sm', !notification.read_at && 'font-semibold')}>
                  {!notification.read_at && <span aria-hidden className="mt-1.5 size-2 shrink-0 rounded-full bg-primary" />}
                  <span className="min-w-0 flex-1 break-words">{notification.text}</span>
                </span>
                <span className="text-xs text-muted-foreground">{formatRelative(notification.created_at)}</span>
              </DropdownMenuItem>
            ))}
          </div>
        ) : (
          <p className="px-2 py-6 text-center text-sm text-muted-foreground">{list.isPending ? t('notifications.bell.loading') : t('notifications.bell.empty')}</p>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
